import {
  BREATH_PHASES,
  breathCycleSeconds,
  type BreathPosition,
  type BreathworkPattern,
} from '@mlm/shared/coaching';
import type { BreathSessionStatus } from '../lib/use-breath-session';

const R = 146;
const C = 2 * Math.PI * R;
const EASE = 'cubic-bezier(0.37,0,0.63,1)';

/*
 * The orb's light, converted from the handoff's oklch (the WebView floor
 * predates oklch): a pale gold highlight over two greens fading to nothing.
 */
const ORB_FILL =
  'radial-gradient(circle at 30% 26%, rgba(212,215,145,0.8) 0%, rgba(56,150,77,0.92) 32%, rgba(0,87,52,0.85) 66%, rgba(0,57,31,0) 100%)';
const ORB_GLOW = '0 0 90px 14px rgba(59,149,85,0.35), inset 0 0 40px rgba(240,242,204,0.18)';
const CUE_SHADOW = '0 1px 14px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.4)';

/**
 * The breathing pacer (2026-10 redesign of the ring): an outer ring with one
 * arc per phase, as long as the phase lasts, the current one in gold; a dot
 * travelling round it through the breath; and an orb that grows on the inhale
 * and shrinks on the exhale. Timing is entirely `useBreathSession`'s: the orb
 * heads for `ring.scale` over `ring.durationMs`, so a late tick still lands on
 * time. Reduced motion drops the transitions and the orb jumps between sizes.
 */
export function BreathPacer({
  pattern,
  status,
  position,
  ring,
  cue,
}: {
  pattern: BreathworkPattern;
  status: BreathSessionStatus;
  position: BreathPosition;
  ring: { scale: number; durationMs: number };
  cue: string;
}) {
  const active = status === 'running' || status === 'paused';
  const cycleMs = breathCycleSeconds(pattern) * 1000;
  const totalSeconds = cycleMs / 1000;

  let cum = 0;
  const arcs = BREATH_PHASES.flatMap((phase) => {
    const seconds = pattern[phase];
    if (seconds <= 0 || totalSeconds <= 0) return [];
    const len = (seconds / totalSeconds) * C;
    const offset = -(cum + 8);
    cum += len;
    return [
      {
        phase,
        dash: `${Math.max(0, len - 16)} ${C}`,
        offset,
        current: active && position.phase === phase,
      },
    ];
  });

  const into = cycleMs > 0 ? position.elapsedMs - (position.cycle - 1) * cycleMs : 0;
  const theta =
    -Math.PI / 2 + 2 * Math.PI * Math.max(0, Math.min(1, cycleMs > 0 ? into / cycleMs : 0));
  const dot = { x: 160 + R * Math.cos(theta), y: 160 + R * Math.sin(theta) };
  const count = Math.max(1, Math.ceil((position.phaseMs - position.phaseElapsedMs) / 1000));
  const grow = {
    transform: `scale(${ring.scale})`,
    transitionProperty: 'transform',
    transitionTimingFunction: EASE,
    transitionDuration: `${Math.max(0, ring.durationMs)}ms`,
  };

  return (
    <div className="relative mx-auto mt-[26px] h-[300px] w-[300px] max-w-full shrink-0">
      <div className="absolute inset-0 flex items-center justify-center" aria-hidden>
        <div
          className="absolute h-[252px] w-[252px] rounded-full border border-paper/[0.14]"
          style={grow}
        />
        <div
          className="h-[196px] w-[196px] rounded-full"
          style={{ ...grow, background: ORB_FILL, boxShadow: ORB_GLOW }}
        />
      </div>
      <svg
        viewBox="0 0 320 320"
        className="absolute inset-0 h-full w-full overflow-visible"
        aria-hidden
      >
        <circle
          cx="160"
          cy="160"
          r={R}
          fill="none"
          stroke="rgba(246,244,238,0.08)"
          strokeWidth="6"
        />
        {arcs.map((a) => (
          <circle
            key={a.phase}
            cx="160"
            cy="160"
            r={R}
            fill="none"
            stroke={a.current ? '#E6C87A' : 'rgba(246,244,238,0.3)'}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={a.dash}
            strokeDashoffset={a.offset}
            transform="rotate(-90 160 160)"
          />
        ))}
        {active && (
          <g
            style={{ transition: 'transform 150ms linear' }}
            transform={`translate(${dot.x} ${dot.y})`}
          >
            <circle r="14" fill="rgba(230,200,122,0.25)" />
            <circle r="6" fill="#E6C87A" />
          </g>
        )}
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
        <span
          aria-live="polite"
          className="font-display text-2xl font-medium leading-[1.1] text-paper"
          style={{ textShadow: CUE_SHADOW }}
        >
          {cue}
        </span>
        {status === 'running' && (
          <span
            aria-hidden
            className="mt-0.5 font-mono text-[34px] font-medium tabular-nums text-paper"
            style={{ textShadow: CUE_SHADOW }}
          >
            {count}
          </span>
        )}
      </div>
    </div>
  );
}
